import React, { useState } from 'react';
import { CalendarDays, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

export function EventCard({ event }) {
    const [showModal, setShowModal] = useState(false);
    const [addedToCalendar, setAddedToCalendar] = useState(false);

    // Support both date_time and start_date_time field names
    const dateTimeField = event.date_time || event.start_date_time;
    const eventDate = new Date(dateTimeField);
    const formattedDate = eventDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
    const formattedTime = eventDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
    });

    const handleAddToCalendar = () => {
        setAddedToCalendar(true);
        setTimeout(() => {
            setAddedToCalendar(false);
        }, 2000);
    };

    const handleBookNow = () => {
        if (event.booking_url) {
            window.open(event.booking_url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <>
            <div
                className="card-interactive overflow-hidden"
                onClick={() => setShowModal(true)}
                data-testid={`event-card-${event.id}`}
            >
                {/* Event Image */}
                {event.image_url && (
                    <div className="w-full h-40 -mx-4 -mt-4 mb-4 overflow-hidden">
                        <img 
                            src={event.image_url} 
                            alt={event.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>
                )}
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-primary flex flex-col items-center justify-center text-white">
                        <span className="text-2xl font-bold leading-none">{eventDate.getDate()}</span>
                        <span className="text-xs uppercase">{eventDate.toLocaleString('en-GB', { month: 'short' })}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-foreground mb-1 truncate">{event.title}</h3>
                        <div className="flex items-center gap-1 text-muted-foreground text-sm mb-1">
                            <Clock className="w-4 h-4" />
                            <span>{formattedTime}</span>
                            {event.price && (
                                <>
                                    <span className="mx-1">•</span>
                                    <span className="font-medium text-primary">{event.price}</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground text-sm">
                            <MapPin className="w-4 h-4" />
                            <span>{event.location}</span>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-md mx-4 rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="event-modal">
                    {/* Modal Image */}
                    {event.image_url && (
                        <div className="w-full h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-3xl">
                            <img 
                                src={event.image_url} 
                                alt={event.title}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    <DialogHeader>
                        <DialogTitle className="text-2xl">{event.title}</DialogTitle>
                        <DialogDescription asChild>
                            <div className="space-y-4 pt-4">
                                <div className="flex items-center gap-3 text-foreground">
                                    <CalendarDays className="w-5 h-5 text-primary" />
                                    <span>{formattedDate}</span>
                                </div>
                                <div className="flex items-center gap-3 text-foreground">
                                    <Clock className="w-5 h-5 text-primary" />
                                    <span>{formattedTime}</span>
                                </div>
                                <div className="flex items-center gap-3 text-foreground">
                                    <MapPin className="w-5 h-5 text-primary" />
                                    <span>{event.location}</span>
                                </div>
                                {event.price && (
                                    <div className="flex items-center gap-3 text-foreground">
                                        <span className="w-5 h-5 text-primary font-bold text-center">£</span>
                                        <span className="font-medium">{event.price}</span>
                                    </div>
                                )}
                                <p className="text-muted-foreground leading-relaxed pt-2">
                                    {event.description}
                                </p>
                                {(event.contact_phone || event.contact_email) && (
                                    <div className="pt-3 border-t border-border">
                                        <p className="text-sm font-medium text-foreground mb-1">Contact:</p>
                                        {event.contact_phone && (
                                            <a href={`tel:${event.contact_phone}`} className="text-sm text-primary block">{event.contact_phone}</a>
                                        )}
                                        {event.contact_email && (
                                            <a href={`mailto:${event.contact_email}`} className="text-sm text-primary block">{event.contact_email}</a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-6 space-y-3">
                        {/* Book Now Button - Links to Burston website */}
                        {event.booking_url && (
                            <Button
                                onClick={handleBookNow}
                                className="w-full h-14 rounded-full text-lg font-bold"
                                data-testid="book-now-btn"
                            >
                                <ExternalLink className="w-5 h-5 mr-2" />
                                Book on Burston.co.uk
                            </Button>
                        )}
                        
                        {/* Add to Calendar Button */}
                        {addedToCalendar ? (
                            <div className="bg-primary/10 text-primary font-bold py-4 px-6 rounded-full text-center animate-scale-in">
                                ✓ Added to Calendar!
                            </div>
                        ) : (
                            <Button
                                onClick={handleAddToCalendar}
                                variant={event.booking_url ? "outline" : "default"}
                                className="w-full h-14 rounded-full text-lg font-bold"
                                data-testid="add-to-calendar-btn"
                            >
                                <CalendarDays className="w-5 h-5 mr-2" />
                                Add to Calendar
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default EventCard;
